import { z } from 'zod'
import { runText } from './content-text'

/**
 * What a book is made of.
 *
 * This file is the single source of truth for block and inline content: the
 * database column types, the API's validation, the schema the model is shown
 * over MCP and the renderer's props all derive from these schemas. Writing the
 * shape twice is how a renderer ends up handling a case the validator rejects.
 *
 * The governing restriction, and the reason typed JSON is safe here:
 *
 *   **JSON holds text. Foreign keys hold relationships.**
 *
 * Nothing below stores a relationship. A note marker, a citation and a
 * cross-reference are all one thing here — a CALL, which says only that one sits
 * at this point in the words. What it names is a `studio_call` row with a real
 * foreign key, so a marker cannot point at a note the book does not hold, and a
 * dangling reference is a constraint violation at write time rather than a wrong
 * number on a printed page.
 *
 * Which call a placeholder is, is its POSITION among the calls in these words.
 * So the order the runs below are read in is load-bearing, and `runsOf` is the
 * one place that states it.
 */

// ---------------------------------------------------------------------------
// Inline content
// ---------------------------------------------------------------------------

/**
 * Marks are what a run IS, not how it looks.
 *
 * `emphasis` rather than `italic`: emphasis is the author's meaning, and italic
 * is only how English happens to say it. Hebrew says it another way — see
 * `emphasisIsItalic` below.
 */
export const markSchema = z.enum([
  'emphasis',
  'strong',
  'smallcaps',
  'transliteration',
])
export type Mark = z.infer<typeof markSchema>

/**
 * The language a run is written in.
 *
 * This is the load-bearing field of the whole model. Face selection and
 * bidirectional isolation are read off it rather than guessed from the
 * characters: Unicode ranges cannot tell pure Hebrew from Hebrew mixed into an
 * English sentence, and those two take different faces.
 *
 * `mixed` is a deliberate third value, not an absence. It marks a run where
 * Hebrew and English are set together as one phrase — a name beside its Hebrew
 * form — which takes a single family carrying both scripts so the pair does not
 * split across two typefaces.
 *
 * **It does not mean "these characters are of two scripts".** That is an
 * observation anything can make by reading the words, and `runSegments` makes
 * it at render, on the runs that state nothing. Storing it here would spend the
 * one authoritative field on a fact already in the text — and because a stored
 * language is authoritative, it would also switch that derivation off on
 * exactly the runs it was written for.
 */
export const langSchema = z.enum(['en', 'he', 'aramaic', 'mixed'])
export type Lang = z.infer<typeof langSchema>

/**
 * Whether emphasis is set as italic on this run — and it is English only.
 *
 * Hebrew has no italic tradition and no italic to set: slanting a Hebrew face
 * produces a mechanical distortion of letterforms that were never drawn to
 * lean, and it reads as a rendering fault rather than as emphasis. IBM Plex
 * Sans Hebrew ships no italic at all, which is the same fact stated by the
 * people who drew it.
 *
 * A `mixed` run is Hebrew and English set as one phrase, so it follows the
 * Hebrew: the alternative slants the Hebrew half of a phrase to emphasize the
 * English half of it.
 *
 * Emphasis on a Hebrew run is therefore carried some other way — spacing, or a
 * weight, or nothing where the words are already set apart. What it is never
 * carried by is a slant.
 */
export const emphasisIsItalic = (lang: Lang | undefined): boolean =>
  lang === undefined || lang === 'en'

/** What a call names — a note, a source, or somewhere else in the book. */
export const callKindSchema = z.enum(['note', 'citation', 'xref'])
export type CallKind = z.infer<typeof callKindSchema>

/**
 * A call the words make, and what it names.
 *
 * A schema as well as a type, because this crosses the wire: an edit that moves
 * or deletes a marker sends the calls that survived it, and what a caller says a
 * marker names decides which note a reader is sent to.
 */
export const callSchema = z.object({ of: callKindSchema, id: z.uuid() })
export type Call = z.infer<typeof callSchema>

export const inlineSchema = z.discriminatedUnion('t', [
  z.object({
    t: z.literal('text'),
    v: z.string(),
    lang: langSchema.optional(),
    marks: z.array(markSchema).optional(),
  }),
  /**
   * A call sits here — a note marker, a citation, a cross-reference.
   *
   * It carries NO id. What it names is a `studio_call` row with a real foreign
   * key, and which call this one is, is its POSITION among the calls in these
   * words. That is the canon's rule kept: JSON holds text, foreign keys hold
   * relationships, and an id in here would look the same while checking
   * nothing.
   *
   * The KIND stays, because it is a property of the line rather than of the
   * relationship: a renderer laying out a run needs to know a marker is set
   * here, and what it draws differs by kind, neither of which should cost a
   * join.
   */
  z.object({ t: z.literal('call'), of: callKindSchema }),
  /** A hard break WITHIN one thought. Between thoughts, use separate blocks. */
  z.object({ t: z.literal('break') }),
])
export type Inline = z.infer<typeof inlineSchema>

/** A string as the run it stands for — the one place the shortcut expands. */
export const runOfString = (v: string, lang?: Lang): Inline[] => [
  lang ? { t: 'text', v, lang } : { t: 'text', v },
]

/**
 * A run of words.
 *
 * A plain STRING is accepted here and stored as the run it stands for, which is
 * a shortcut for the case with nothing to say and never a second content model.
 * The typed form carries a language, a mark and a call, and a string carries
 * none of the three — so it means exactly "these words, unmarked, in the
 * language this field is": the book's own, or the one the field names.
 *
 * It exists because the cost of the model otherwise lands hardest where there
 * is least to state. A paragraph of ordinary prose is three levels of JSON
 * around one string, none of which says anything, and a caller composing a
 * chapter writes that shape a hundred times before reaching a phrase that
 * actually needed it.
 */
export const inlineRun = z
  .preprocess(
    (v) => (typeof v === 'string' ? runOfString(v) : v),
    z.array(inlineSchema),
  )
  .describe(
    'The words. Either a plain string — unmarked text in this field\'s own language — or typed nodes: {"t":"text","v":"…"} with an optional `lang` and `marks`, {"t":"call","of":"note"} where a marker sits, {"t":"break"} for a hard break inside one thought.',
  )
export type InlineRun = z.infer<typeof inlineRun>

// ---------------------------------------------------------------------------
// Blocks
// ---------------------------------------------------------------------------

/**
 * Whether a run-in heading reaches the table of contents.
 *
 * One predicate serves the editor and the printed contents, so the two cannot
 * disagree about what the book contains.
 */
export const tocBehaviorSchema = z.enum(['normal', 'always', 'never'])
export type TocBehavior = z.infer<typeof tocBehaviorSchema>

export const blockContentSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('paragraph'),
      content: inlineRun,
      /** Opens a section: no first-line indent, by the usual typographic rule. */
      lead: z.boolean().optional(),
    })
    .describe('Running prose. The ordinary passage.'),

  /**
   * A list of propositions, one per line.
   *
   * Not a paragraph with hard breaks. The manuscript's summations are stacks of
   * claims, and each is a unit: it can be cited on its own, it must not be
   * split across a page, and it can be reordered without disturbing its
   * neighbors. Stored as a paragraph, all three become impossible.
   */
  z
    .object({
      kind: z.literal('claim_list'),
      items: z.array(inlineRun),
    })
    .describe(
      'A stack of propositions, one per item — not a paragraph with hard breaks. Each can be cited alone and must not split across a page. It carries no bullet and no number: a number is computed by walking the list, and is never set here.',
    ),

  /**
   * Scripture, set apart. `en` is a translation of `he`, not a separate text —
   * which is why they are one block and not two.
   */
  z
    .object({
      kind: z.literal('verse'),
      /**
       * Where the passage comes from — "Genesis 4:26", "Birkat HaTorah".
       *
       * A field rather than a heading above the verse, so what a passage IS and
       * where it comes from are one record: joined by adjacency, a reference is
       * separated by a reorder and stranded above its verse by a page break, and
       * nothing can answer what a passage cites.
       *
       * An inline run rather than a plain string, which is what separates it from
       * `classification`. A reference is a NAME, and the names in this material
       * are routinely Hebrew or a Hebrew work beside its transliteration — so it
       * needs the language that decides the face and the direction, which only a
       * run carries. A classification is a sentence of apparatus in the book's own
       * language, and a string states that exactly.
       */
      reference: inlineRun.optional(),
      he: inlineRun,
      en: inlineRun.optional(),
      /** The addendum's per-verse note on what the passage exemplifies. */
      classification: z.string().optional(),
      /**
       * How the Hebrew is set: centered display type, or to the measure.
       *
       * `centered` is right for the two lines of a scriptural verse and wrong for
       * a passage that runs to twenty: centering a page of Hebrew leaves both edges
       * ragged and loses the reader the line start on every return. `block` sets
       * it justified to the measure, still in the display face.
       */
      display: z.enum(['centered', 'block']).default('centered'),
    })
    .describe(
      'Scripture, set apart. `en` is a TRANSLATION of `he` and not a separate text, which is why they are one block. `reference` says where the passage comes from — it belongs to the verse rather than to a heading above it.',
    ),

  /**
   * A quotation from a work, in the original and in translation.
   *
   * The source is not a field here: attribution is a `citation` row, so the
   * same work cited sixty times is one record rather than sixty strings that
   * can disagree about its name.
   */
  z
    .object({
      kind: z.literal('source_quote'),
      he: inlineRun,
      en: inlineRun,
      /**
       * Who is being quoted, carried by the quote itself.
       *
       * Held here rather than in a block after it, because a source line beneath
       * a quotation is PART of it: filed separately the two are joined only by
       * standing next to each other, which a reorder breaks, a page break divides,
       * and a surface that wraps its blocks silently pulls apart.
       */
      attribution: inlineRun.optional(),
    })
    .describe(
      'A quotation from a work, in the original and in translation. WHICH work is a citation row rather than a field here — `book_cite` writes it — so the same work quoted sixty times is one record. `attribution` is the source line the quotation itself carries.',
    ),

  z
    .object({
      kind: z.literal('quotation'),
      content: inlineRun,
      attribution: inlineRun.optional(),
    })
    .describe('A quotation in one language.'),

  /**
   * A heading inside body flow — a keyword the passage then unpacks.
   *
   * A block rather than a section: it has no children and carries no numbering,
   * and making it a section would multiply the tree by nodes that exist only to
   * be printed.
   */
  z
    .object({
      kind: z.literal('run_in_heading'),
      content: inlineRun,
      tocBehavior: tocBehaviorSchema.default('never'),
    })
    .describe(
      'A heading inside the body flow — a keyword the passage then unpacks. A block rather than a section: it holds no children and carries no numbering. Where a heading DOES hold what follows it, add a section instead.',
    ),

  /**
   * An exchange between named speakers.
   *
   * The driving manuscript writes this as headings and horizontal rules, and
   * the contents page proves that is presentation rather than structure: it
   * lists the chapter's parts and never the speakers. Typed turns let the
   * speaker be styled, indexed and translated as what it is.
   *
   * **A turn may name no speaker, and that means the one before it continues.**
   * A speech is interrupted by anything a turn cannot hold — chiefly a line set
   * apart, which is a `verse` and therefore a block — so the words after it
   * resume rather than begin. Naming the speaker again would print a second
   * label and read as a reply; leaving the field empty would print an empty
   * line where a label goes. The absence IS the statement.
   */
  z
    .object({
      kind: z.literal('dialogue'),
      turns: z.array(
        z.object({
          /*
           * Absent or said — never blank. An empty run prints the label element
           * with nothing in it, which is the blank line the rule above rejects,
           * and it offers an editor a speaker path that says nothing. The
           * string shortcut makes it reachable by accident: `""` parses to a
           * run holding one empty node, which is truthy everywhere it is
           * tested.
           */
          speaker: inlineRun
            .refine(
              (run) => runText(run) !== '',
              'A turn names a speaker or names none — an empty one prints a blank line where a name goes.',
            )
            .optional(),
          /** The turn's paragraphs, in order. */
          content: z.array(inlineRun),
        }),
      ),
    })
    .describe(
      'An exchange between named speakers. One block holds the whole exchange rather than a block per turn, and a speaker is a LABEL rather than a section — the contents page lists the parts of a chapter and never the speakers. Each turn is {"speaker":"…","content":["first paragraph","second"]}. OMIT `speaker` to say the speaker before carries on: that is how a speech that was interrupted by a line set apart — a `verse` between two dialogue blocks — resumes without printing a second label.',
    ),

  /**
   * A break in the flow, and what KIND of break decides its mark.
   *
   * A thematic break rests the argument; a turn break separates one speaker
   * from the next inside a dialogue. Two breaks doing different work read as
   * one when they print the same rule.
   */
  z
    .object({
      kind: z.literal('divider'),
      variant: z.enum(['thematic', 'turn']).default('thematic'),
    })
    .describe(
      'A break in the flow. `thematic` rests the argument; `turn` separates one speaker from the next.',
    ),

  z
    .object({
      kind: z.literal('figure'),
      assetId: z.uuid(),
      caption: inlineRun.optional(),
    })
    .describe('An image, by the id of an asset already held.'),
])
export type BlockContent = z.infer<typeof blockContentSchema>
export type BlockKind = BlockContent['kind']

export const BLOCK_KINDS = [
  'paragraph',
  'claim_list',
  'verse',
  'source_quote',
  'quotation',
  'run_in_heading',
  'dialogue',
  'divider',
  'figure',
] as const satisfies readonly BlockKind[]

// ---------------------------------------------------------------------------
// Parts
// ---------------------------------------------------------------------------

/**
 * What a part's heading says.
 *
 * A part and a passage are both nodes over text, so what a part is CALLED is
 * content like any other rather than columns beside it. Each part kind takes a
 * discriminant of its own, which is what lets one union cover a book's every
 * node and lets the database go on checking that a node's kind agrees with the
 * words it points at.
 */
const heading = <K extends string>(kind: K) =>
  z.object({
    kind: z.literal(kind),
    title: inlineRun,
    subtitle: inlineRun.optional(),
    /** What a running head says — "Chapter Three", not the full title. */
    runningHead: z.string().optional(),
  })

export const PART_KINDS = [
  'front_matter',
  'division',
  'part',
  'subsection',
  'notes',
  'addendum',
  'back_matter',
] as const
export type PartKind = (typeof PART_KINDS)[number]

/**
 * Everything a book's text can be — a passage's content or a part's heading.
 *
 * One union over both, because a node holds one or the other and the row it
 * points at is the same kind of row either way. The discriminant is what the
 * node IS, so a reader switches once.
 */
export const nodeContentSchema = z.discriminatedUnion('kind', [
  ...blockContentSchema.options,
  heading('front_matter'),
  heading('division'),
  heading('part'),
  heading('subsection'),
  heading('notes'),
  heading('addendum'),
  heading('back_matter'),
])
export type NodeContent = z.infer<typeof nodeContentSchema>
export type NodeKind = NodeContent['kind']

/**
 * A part's heading, whatever kind of part it is.
 *
 * Named so a caller reading a title has one type to hold rather than a union of
 * seven that differ only in their discriminant.
 */
export type Heading = Extract<NodeContent, { kind: PartKind }>

export const isHeading = (c: NodeContent): c is Heading =>
  (PART_KINDS as readonly string[]).includes(c.kind)

/**
 * Every kind a node can be, in reading-order groups: the parts that hold, then
 * the passages that are held.
 *
 * A note is absent deliberately — a note holds no words of its own, because its
 * body is its children.
 */
export const NODE_KINDS = [
  ...PART_KINDS,
  ...BLOCK_KINDS,
] as const satisfies readonly NodeKind[]
